const pageRoot = document.documentElement;

// Hover follows the pointer in use. Touchscreen laptops report no hover even while their trackpad
// moves a cursor, so a moving mouse, trackpad or pen turns hover styles on and a finger turns them off.
function revealHoverMedia(scope) {
  scope.querySelectorAll('source[data-hover-media]').forEach((source) => {
    source.removeAttribute('data-hover-media');
    source.media = 'all';
  });
}
function setPointer(mode) {
  if (pageRoot.dataset.pointer === mode) return;
  pageRoot.dataset.pointer = mode;
  if (mode === 'fine') revealHoverMedia(document);
}
setPointer(matchMedia('(hover: hover) and (pointer: fine)').matches ? 'fine' : 'touch');
document.addEventListener(
  'pointermove',
  (event) => {
    if (event.pointerType !== 'touch') setPointer('fine');
  },
  { passive: true },
);
// Smooth in-page scrolling starts with the shopper's first press or key (base.css), so the browser's
// restore on reload or Back lands at once instead of gliding down from the top.
function allowSmoothScroll() {
  pageRoot.dataset.scrollSmooth = '';
  document.removeEventListener('pointerdown', allowSmoothScroll, true);
  document.removeEventListener('keydown', allowSmoothScroll, true);
}
document.addEventListener('pointerdown', allowSmoothScroll, { capture: true, passive: true });
document.addEventListener('keydown', allowSmoothScroll, { capture: true, passive: true });
// Cards rendered later, such as recommendations or filtered results, load their second image on first hover.
document.addEventListener(
  'pointerover',
  (event) => {
    if (pageRoot.dataset.pointer !== 'fine') return;
    const card = event.target.closest?.('.product-card');
    if (card) revealHoverMedia(card);
  },
  { passive: true },
);

// Soft press for every pointer: controls shrink a little and dim; wide controls and images move less.
// A finger waits a beat so a scroll that starts on a link never flashes it; moving or cancelling releases.
// Shopify's Follow on Shop element keeps its button in a shadow root, so a press reaches it as the element itself.
const PRESSABLE =
  'a[href], button, summary, [role="option"], label.options-swatch, label.variant-value, shopify-account, .footer-follow:not([inert]) > *';
let press = null;
// Quick taps that are still settling; a new press on the same control continues them instead of being ended.
const settlingTaps = new WeakMap();
function pressTier(element) {
  // A product card's whole image frame shrinks; a brand card scales only its picture.
  if (element.matches('.product-card__media')) return 'card';
  if (element.matches('.brand-showcase__content')) return 'media';
  if (element.offsetWidth <= 280) return '';
  return element.querySelector('img, picture, video, model-viewer') ? 'image' : 'wide';
}
function showPress(element, x, y) {
  const tier = pressTier(element);
  if (tier) element.dataset.pressTier = tier;
  else delete element.dataset.pressTier;
  // Shrink toward the pressed point, so a press near an edge stays on the control and its click still lands.
  // A control grown on hover (--rest-scale) is scaled about its centre: take the point in its unscaled box and
  // shift the control by the difference, so moving the origin there moves nothing, now or when the press ends.
  // Reduced motion keeps a control at its size even where a hover rule sets --rest-scale, so it is never shifted.
  const box = element.getBoundingClientRect();
  if (box.width && box.height) {
    const style = getComputedStyle(element);
    const rest = style.scale === 'none' ? 1 : Number.parseFloat(style.getPropertyValue('--rest-scale')) || 1;
    const originX = (x - box.left) / rest;
    const originY = (y - box.top) / rest;
    element.style.setProperty('--press-origin', `${originX}px ${originY}px`);
    if (rest !== 1) {
      const shiftX = (rest - 1) * (originX - element.offsetWidth / 2);
      const shiftY = (rest - 1) * (originY - element.offsetHeight / 2);
      element.style.setProperty('--press-shift', `${shiftX}px ${shiftY}px`);
      element.dataset.pressShift = '';
    } else {
      element.style.removeProperty('--press-shift');
      delete element.dataset.pressShift;
    }
  }
  element.dataset.pressed = 'in';
}
// 2026-11-04 (Owner): a released button settles on a 420 ms spring and a link or product card eases back in 260 ms
// (base.css); the press marks stay until the release has ended, and reduced motion has none to wait for.
function releaseTime(element) {
  const style = getComputedStyle(element);
  if (style.animationName === 'none') return 0;
  return Math.max(
    0,
    ...style.animationDuration
      .split(',')
      .map((value) => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000) || 0),
  );
}
function endPress(element) {
  element.dataset.pressed = 'out';
  setTimeout(
    () => {
      if (element.dataset.pressed !== 'out') return;
      delete element.dataset.pressed;
      delete element.dataset.pressTier;
      delete element.dataset.pressShift;
      element.style.removeProperty('--press-origin');
      element.style.removeProperty('--press-shift');
    },
    releaseTime(element) + 20,
  );
}
function settleTap(element, delay) {
  clearTimeout(settlingTaps.get(element));
  settlingTaps.set(
    element,
    setTimeout(() => {
      settlingTaps.delete(element);
      endPress(element);
    }, delay),
  );
}
function beginPress(event) {
  cancelCardOpening();
  releasePress(true);
  if (!event.isPrimary || event.button > 0) return;
  const element = event.target.closest?.(PRESSABLE);
  if (!element || element.matches(':disabled, [aria-disabled="true"]')) return;
  if (element.matches('label') && element.querySelector(':disabled')) return;
  const touch = event.pointerType === 'touch';
  const current = {
    element,
    pointer: event.pointerId,
    x: event.clientX,
    y: event.clientY,
    shown: false,
    timer: 0,
    touch,
  };
  press = current;
  const settling = settlingTaps.get(element);
  if (settling || !touch) {
    clearTimeout(settling);
    settlingTaps.delete(element);
    current.shown = true;
    showPress(element, current.x, current.y);
    return;
  }
  current.timer = setTimeout(() => {
    if (press !== current) return;
    current.shown = true;
    showPress(element, current.x, current.y);
    warmProductPage(element);
  }, 70);
}
function releasePress(cancelled) {
  if (!press) return;
  const { element, timer, shown, x, y } = press;
  press = null;
  clearTimeout(timer);
  if (shown) endPress(element);
  else if (!cancelled) {
    // A quick tap still shows the press before it lets go.
    showPress(element, x, y);
    settleTap(element, 110);
    warmProductPage(element);
  }
}
document.addEventListener(
  'pointermove',
  (event) => {
    if (press?.pointer !== event.pointerId) return;
    if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > 10) releasePress(true);
  },
  { capture: true, passive: true },
);

// Capture the input before native focus and component focus restoration run.
// Pointer interactions keep structural borders, without keyboard focus rings.
let pressedArrowButton = null;
let pressedArrowPointer = null;
function clearPressedArrow() {
  pressedArrowButton?.removeAttribute('data-arrow-pressed');
  pressedArrowButton = null;
  pressedArrowPointer = null;
}
function clearPressFeedback() {
  clearPressedArrow();
  releasePress(true);
}
document.addEventListener(
  'pointerdown',
  (event) => {
    pageRoot.dataset.focusInput = 'pointer';
    setPointer(event.pointerType === 'touch' ? 'touch' : 'fine');
    beginPress(event);
    if (event.pointerType === 'mouse' || !event.isPrimary) return;
    clearPressedArrow();
    const button = event.target.closest('.button');
    if (!button?.querySelector('.icon-arrow-right')) return;
    // Touch browsers do not consistently expose :active while a finger is down.
    // Keep feedback transient without cancelling scrolling or delaying navigation.
    pressedArrowButton = button;
    pressedArrowPointer = event.pointerId;
    button.setAttribute('data-arrow-pressed', '');
  },
  { capture: true, passive: true },
);
for (const type of ['pointerup', 'pointercancel']) {
  document.addEventListener(
    type,
    (event) => {
      if (event.pointerId === pressedArrowPointer) clearPressedArrow();
      if (event.pointerId !== press?.pointer) return;
      cardTap =
        type === 'pointerup' && press.touch && press.element.matches(CARD_LINKS)
          ? { link: press.element, time: event.timeStamp }
          : null;
      releasePress(type === 'pointercancel');
    },
    { capture: true, passive: true },
  );
}
window.addEventListener('blur', clearPressFeedback);
window.addEventListener('pagehide', clearPressFeedback);

// 2.8.3 (Owner): a finger's tap on a product card lets the press spring back before the product page opens. Safari
// stops drawing a page as soon as it starts to leave it, so the card would stay pressed until the product page
// appeared. The release (base.css) looks finished about 200 ms into its 260 ms; reduced motion releases at once and
// waits only for that frame. Mice, keys, new-tab clicks and in-page links open at once.
const CARD_LINKS = '.product-card__media[href], .product-card__title a[href]';
let cardTap = null;
let cardOpening = 0;
function cancelCardOpening() {
  clearTimeout(cardOpening);
  cardOpening = 0;
}
window.addEventListener('click', (event) => {
  // A later action owns the page, including native links still waiting for a response and quick-media dialogs.
  cancelCardOpening();
  const tap = cardTap;
  cardTap = null;
  const link = event.target.closest?.(CARD_LINKS);
  if (!link || link !== tap?.link || event.timeStamp - tap.time > 1000 || event.defaultPrevented) return;
  if (event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
  if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;
  const url = new URL(link.href);
  if (url.hash && url.href.split('#')[0] === location.href.split('#')[0]) return;
  event.preventDefault();
  // A quick tap shows its press for a moment instead of settling for the whole 110 ms.
  let release = 0;
  if (settlingTaps.has(link)) {
    release = 70;
    settleTap(link, release);
  }
  cardOpening = setTimeout(() => location.assign(url), release + (reducedMotion.matches ? 50 : 200));
});
window.addEventListener('pagehide', cancelCardOpening);
// A finger resting on a product card, or tapping it, also fetches the image its product page shows first. The card
// keeps that image in a template rendered like the product gallery's first image, so the product page finds the same
// file in the cache; Safari cannot fetch the page itself early (theme.liquid speculation rules). A finger that moves
// on to scroll fetches nothing, and Save-Data skips it.
const warmedCards = new WeakSet();
const pageImages = new Set();
function warmProductPage(link) {
  if (!link.matches(CARD_LINKS) || navigator.connection?.saveData) return;
  const card = link.closest('.product-card');
  const image = card?.querySelector('template[data-page-image]')?.content.querySelector('img');
  if (!image || warmedCards.has(card)) return;
  warmedCards.add(card);
  // Held until it loads, so the download is not dropped with an unreferenced element.
  const copy = document.importNode(image);
  pageImages.add(copy);
  const done = () => pageImages.delete(copy);
  copy.addEventListener('load', done, { once: true });
  copy.addEventListener('error', done, { once: true });
}
document.addEventListener(
  'keydown',
  (event) => {
    cancelCardOpening();
    if (!event.altKey && !event.ctrlKey && !event.metaKey && !event.isComposing)
      pageRoot.dataset.focusInput = 'keyboard';
  },
  { capture: true },
);

// Menus, dropdown lists and the cart drawer hide their scrollbar. Mark which ends of a list still hold content so a
// small arrow can show there; lists are re-read when they open, filter, change content, resize or scroll.
const HINTED_LISTS = [
  '.site-menu > nav',
  '.nav-dropdown > div',
  '.quick-dropdown > div',
  '.localization__list',
  '.options-variant-popover',
  // 3.0.0 (Owner): an add-on dropdown's list scrolls like the region and language list.
  '.options-dropdown__list',
  // 2.9.9 (Owner): the add-on description window scrolls like the region and language list.
  '.options-help-body',
  '.filter-panel',
  'meri-predictive-search [data-predictive-panel]',
  '.cart-drawer',
].join(', ');
const watchedLists = new WeakSet();
const listChanges = new MutationObserver(() => markListEnds());
const listSizes = new ResizeObserver(() => markListEnds());
let listFrame = 0;
function markEnds(list) {
  const room = list.scrollHeight - list.clientHeight;
  list.toggleAttribute('data-more-above', room > 1 && list.scrollTop > 1);
  list.toggleAttribute('data-more-below', room > 1 && list.scrollTop < room - 1);
}
function markListEnds() {
  cancelAnimationFrame(listFrame);
  listFrame = requestAnimationFrame(() => {
    for (const list of document.querySelectorAll(HINTED_LISTS)) {
      if (!watchedLists.has(list)) {
        watchedLists.add(list);
        listChanges.observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
        listSizes.observe(list);
      }
      markEnds(list);
    }
  });
}
document.addEventListener(
  'scroll',
  (event) => {
    if (event.target.matches?.(HINTED_LISTS)) markEnds(event.target);
  },
  { capture: true, passive: true },
);
for (const type of ['toggle', 'input']) document.addEventListener(type, markListEnds, { capture: true, passive: true });
window.addEventListener('resize', markListEnds, { passive: true });
markListEnds();

// A tap on a showing arrow scrolls its list all the way to that end. The arrow is the list's own ::before or
// ::after (32px, 56px on phones, centred), so a press on it reaches the list itself; where it sits tells which
// end it is.
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
let hintTap = null;
function hintAt(event) {
  const list = event.target;
  if (!(list instanceof Element) || !list.matches(HINTED_LISTS)) return null;
  const style = getComputedStyle(list);
  const box = list.getBoundingClientRect();
  const left = box.left + list.clientLeft + parseFloat(style.paddingLeft);
  const width = list.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
  const offCentre = Math.abs(event.clientX - (left + width / 2));
  // An arrow sticks to its end of the visible area, or just inside a sticky header there such as the cart drawer's;
  // some browsers keep it inside the list's padding, so the reach is that padding plus the arrow. Rows there are
  // still their own targets, never the list.
  const top = box.top + list.clientTop;
  const fromTop = event.clientY - top;
  const fromBottom = top + list.clientHeight - event.clientY;
  const reaches = (pseudo, fromEnd, padding) => {
    const arrow = getComputedStyle(list, pseudo);
    if (arrow.content === 'none') return false;
    const size = parseFloat(arrow.width);
    const reach = fromEnd - (parseFloat(pseudo === '::before' ? arrow.top : arrow.bottom) || 0);
    return size > 0 && offCentre <= size / 2 + 1 && reach >= 0 && reach <= parseFloat(padding) + size + 1;
  };
  if (list.hasAttribute('data-more-above') && reaches('::before', fromTop, style.paddingTop))
    return { list, end: 'top' };
  if (list.hasAttribute('data-more-below') && reaches('::after', fromBottom, style.paddingBottom))
    return { list, end: 'bottom' };
  return null;
}
function releaseHint() {
  const tap = hintTap;
  hintTap = null;
  // A quick tap still shows the arrow settle before it lets go.
  if (tap)
    setTimeout(() => {
      if (hintTap?.list !== tap.list) delete tap.list.dataset.hintPress;
    }, 90);
}
document.addEventListener(
  'pointerdown',
  (event) => {
    releaseHint();
    if (!event.isPrimary || event.button > 0) return;
    const hit = hintAt(event);
    if (!hit) return;
    hintTap = { ...hit, pointer: event.pointerId, x: event.clientX, y: event.clientY };
    hit.list.dataset.hintPress = hit.end;
  },
  { capture: true, passive: true },
);
// The arrow never takes focus, so a search field stays active and its suggestions stay open.
document.addEventListener(
  'mousedown',
  (event) => {
    if (hintAt(event)) event.preventDefault();
  },
  { capture: true },
);
document.addEventListener(
  'pointermove',
  (event) => {
    if (hintTap?.pointer !== event.pointerId) return;
    if (Math.hypot(event.clientX - hintTap.x, event.clientY - hintTap.y) > 10) releaseHint();
  },
  { capture: true, passive: true },
);
document.addEventListener(
  'pointerup',
  (event) => {
    if (hintTap?.pointer !== event.pointerId) return;
    const { list, end } = hintTap;
    releaseHint();
    list.scrollTo({ top: end === 'top' ? 0 : list.scrollHeight, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  },
  { capture: true, passive: true },
);
document.addEventListener('pointercancel', releaseHint, { capture: true, passive: true });
// A mouse or pen over a showing arrow marks it, so it grows like the theme's round buttons (base.css).
let hintHover = null;
function hoverHint(list, end) {
  if (hintHover && hintHover !== list) delete hintHover.dataset.hintHover;
  hintHover = list;
  if (list && list.dataset.hintHover !== end) list.dataset.hintHover = end;
}
document.addEventListener(
  'pointermove',
  (event) => {
    if (event.pointerType === 'touch') return;
    const hit = hintAt(event);
    hoverHint(hit?.list ?? null, hit?.end);
  },
  { capture: true, passive: true },
);
document.addEventListener(
  'pointerout',
  (event) => {
    if (!event.relatedTarget) hoverHint(null);
  },
  { capture: true, passive: true },
);

// Screen readers hear a cart count change (WCAG 4.1.3); reads that change nothing stay silent.
let announcedCount = null;
document.addEventListener('meri:cart:updated', (event) => {
  const count = event.detail?.cart?.item_count;
  if (!Number.isSafeInteger(count) || count < 0) return;
  document.querySelectorAll('[data-cart-count]').forEach((node) => {
    node.textContent = count;
  });
  const previous = announcedCount ?? Number(document.querySelector('[data-cart-count]')?.dataset.cartCountInitial);
  announcedCount = count;
  const announcer = document.querySelector('[data-cart-announcer]');
  const template = globalThis.MeriStrings?.cartUpdatedQuantity;
  if (!announcer || !template || !Number.isSafeInteger(previous) || previous === count) return;
  announcer.textContent = '';
  requestAnimationFrame(() => {
    announcer.textContent = template.replace('__QUANTITY__', count);
  });
});

document.addEventListener('click', async (event) => {
  const retry = event.target.closest('[data-cart-pricing-retry]');
  if (retry) {
    event.preventDefault();
    // An explicit retry asks for Shopify's cart; a rejected quantity left in a box is not a draft to keep.
    await refreshCartPricing(retry.closest('[data-cart-page]'), undefined, { force: true });
    return;
  }
  const remove = event.target.closest('[data-cart-remove]');
  if (!remove) return;
  event.preventDefault();
  await updateLine(remove, 0);
});
// An empty or malformed entry (cleared box, "-", "1.5") is unfinished typing,
// not a removal request: Number('') is 0. Restore Shopify's rendered quantity so
// removal stays on the explicit Remove control or a deliberate 0.
function enteredQuantity(input) {
  const raw = input.value.trim();
  const quantity = Number(raw);
  if (raw !== '' && Number.isInteger(quantity) && quantity >= 0) return quantity;
  input.value = input.closest('[data-cart-line]')?.dataset.quantity ?? input.defaultValue;
  resumeDeferredPricing(input);
  return null;
}
function resumeDeferredPricing(input) {
  const root = input.closest('[data-cart-page]');
  const state = pricingStates.get(root);
  if (state?.refreshRequested && !state.pending && !deferQuantityDraft(root, state)) scheduleCartPricing(root);
}
document.addEventListener('change', async (event) => {
  if (!event.target.matches('[data-cart-quantity]')) return;
  const quantity = enteredQuantity(event.target);
  if (quantity !== null) await updateLine(event.target, quantity);
});
document.addEventListener('keydown', async (event) => {
  if (event.key !== 'Enter' || !event.target.matches('[data-cart-quantity]')) return;
  event.preventDefault();
  const quantity = enteredQuantity(event.target);
  if (quantity !== null) await updateLine(event.target, quantity);
});
document.addEventListener('input', (event) => {
  if (event.target.matches('[data-cart-quantity]')) resumeDeferredPricing(event.target);
});
const checkoutChecks = new WeakSet();
const approvedCheckoutForms = new WeakSet();
document.addEventListener(
  'submit',
  async (event) => {
    const page = event.target.closest('[data-cart-page]');
    if (!page) return;
    if (['pending', 'stale', 'failed'].includes(page.dataset.cartPricing)) {
      event.preventDefault();
      return;
    }
    const form = event.target;
    if (event.submitter?.name !== 'checkout' || approvedCheckoutForms.has(form)) {
      approvedCheckoutForms.delete(form);
      clearPricingSchedule(pricingStates.get(page));
      return;
    }
    event.preventDefault();
    if (checkoutChecks.has(form)) return;
    checkoutChecks.add(form);
    const submitter = event.submitter;
    const region = page.querySelector('[data-cart-message]');
    try {
      const { cart, validateAccessoryIntegrity } = await loadCartModule();
      validateAccessoryIntegrity(await cart.read());
      if (!form.isConnected || ['pending', 'stale', 'failed'].includes(page.dataset.cartPricing)) return;
      approvedCheckoutForms.add(form);
      form.requestSubmit(submitter);
    } catch {
      if (region) {
        region.textContent = globalThis.MeriStrings?.cartError || 'Review your cart and retry.';
        region.tabIndex = -1;
        region.focus();
      }
    } finally {
      checkoutChecks.delete(form);
    }
  },
  true,
);

const pricingStates = new WeakMap();
function deferQuantityDraft(root, state) {
  // A completed quantity write already changed Shopify's cart. Its rendered rows
  // are stale and the form stays inert until they are replaced, so another
  // row's unsent draft must not hold that render back.
  if (state.forceRender) return false;
  if (
    ![...root.querySelectorAll('[data-cart-quantity]')].some(
      (input) =>
        input.value !== input.closest('[data-cart-line]')?.dataset.quantity &&
        input.dataset.cartCommitted !== input.value,
    )
  )
    return false;
  // Preserve the original input and its native blur/change behavior. Rechecks
  // resume when the draft is restored, or the existing quantity write reloads.
  state.refreshRequested = true;
  clearTimeout(state.timer);
  state.timer = null;
  if (!state.pending && root.dataset.cartPricing === 'pending') root.dataset.cartPricing = 'ready';
  return true;
}
function clearBoundaryChecks(state) {
  for (const timer of state?.boundaryTimers || []) clearTimeout(timer);
  if (state) state.boundaryTimers = [];
}
function clearPricingSchedule(state) {
  if (!state) return;
  clearTimeout(state.timer);
  state.timer = null;
  state.refreshRequested = false;
  clearBoundaryChecks(state);
}
async function loadCartModule() {
  let timer;
  try {
    return await Promise.race([
      import(globalThis.MeriAssets?.cart || './cart.js'),
      new Promise((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error('Cart module timed out')), 15000);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}
export function prepareCartDrawerContent(root) {
  // A background preparation is not a cart view. The official element emits
  // its view event only when the containing dialog opens.
  root.setAttribute('view-event-trigger', 'dialog');
  for (const image of root.querySelectorAll('[data-cart-line] img')) {
    image.sizes = image.closest('.cart-line--child') ? '38px' : '64px';
    image.loading = 'eager';
    image.decoding = 'async';
  }
  return root;
}
export function refreshCartPricing(root, changedCart, { force = false } = {}) {
  if (!root?.isConnected) return;
  let state = pricingStates.get(root);
  if (!state || state.cancelled) {
    state = { root };
    pricingStates.set(root, state);
  }
  if (force) state.forceRender = true;
  if (state.pending) {
    // The running render may have read Shopify before this write; render once more.
    if (force) state.refreshRequested = true;
    return state.task;
  }
  if (document.hidden) {
    state.refreshRequested = true;
    return;
  }
  if (deferQuantityDraft(root, state)) return;
  clearTimeout(state.timer);
  state.timer = null;
  state.refreshRequested = false;
  state.pending = true;
  state.task = renderCartPricing(root, changedCart, state);
  return state.task;
}
async function renderCartPricing(root, changedCart, state) {
  const form = root.querySelector('form');
  const message = root.querySelector('[data-cart-message]');
  const deferredFocus = state.deferredFocus;
  state.deferredFocus = null;
  state.forceRender = false;
  const focused = deferredFocus?.node?.isConnected
    ? deferredFocus.node
    : root.contains(document.activeElement)
      ? document.activeElement
      : null;
  const focusedLine = focused?.closest('[data-cart-line]');
  const lineReference = focusedLine && {
    lineId: focusedLine.dataset.lineId,
    key: focusedLine.dataset.key,
    // External lines may have neither a stable theme id nor an unchanged Shopify key.
    // A variant is a safe focus fallback only when it identifies one row in both snapshots.
    variantId: focusedLine.dataset.variantId,
    uniqueVariant:
      [...root.querySelectorAll('[data-cart-line]')].filter(
        (line) => line.dataset.variantId === focusedLine.dataset.variantId,
      ).length === 1,
    selector: focused.matches('[data-cart-quantity]')
      ? '[data-cart-quantity]'
      : focused.matches('[data-cart-remove]')
        ? '[data-cart-remove]'
        : null,
  };
  const note = form?.elements.namedItem('note');
  const draft = note && note.value !== note.defaultValue ? note.value : undefined;
  const selection =
    note && focused === note
      ? deferredFocus?.node === note
        ? deferredFocus.selection
        : [note.selectionStart, note.selectionEnd]
      : null;
  root.dataset.cartPricing = 'pending';
  if (form) {
    form.dataset.pending = 'true';
    form.inert = true;
    form.setAttribute('aria-busy', 'true');
  }
  if (message) message.textContent = globalThis.MeriStrings?.updating || 'Updating…';
  try {
    const { cart } = await loadCartModule();
    // Recheck visibility after earlier cart writes, before scheduling pricing.
    await cart.serial(() => {});
    if (!root.isConnected || state.cancelled) return;
    if (document.hidden) {
      state.deferredFocus = { node: focused, selection };
      state.refreshRequested = true;
      return;
    }
    const context = new URL(location.href);
    for (const key of ['sections', 'section_id']) context.searchParams.delete(key);
    state.refreshRequested = false; // This request includes boundaries observed while waiting in the queue.
    const load = async (changed) => {
      const value = await cart.refreshPricing(
        {
          sectionId: root.dataset.cartSection,
          sectionsUrl: context.pathname + context.search,
        },
        changed,
      );
      if (!root.isConnected || state.cancelled) return null;
      const parsed = new DOMParser().parseFromString(value.sections[root.dataset.cartSection], 'text/html');
      const next = [...parsed.querySelectorAll('[data-cart-page]')].find(
        (node) => node.dataset.cartSection === root.dataset.cartSection,
      );
      if (!next || !next.querySelector('[data-cart-message]') || (value.item_count && !next.querySelector('form')))
        throw new Error('Missing current cart section');
      cart.confirmPricing(value, {
        total: next.dataset.cartTotalPrice,
        originalTotal: next.dataset.cartOriginalTotalPrice,
        itemsSubtotal: next.dataset.cartItemsSubtotalPrice,
        totalDiscount: next.dataset.cartTotalDiscount,
        currency: next.dataset.cartCurrency,
        itemCount: next.dataset.cartItemCount,
        lines: [...next.querySelectorAll('[data-cart-line]')].map((line) => ({
          key: line.dataset.key,
          lineId: line.dataset.lineId,
          variantId: line.dataset.variantId,
          quantity: line.dataset.quantity,
          originalLinePrice: line.dataset.originalLinePrice,
          finalLinePrice: line.dataset.finalLinePrice,
        })),
      });
      return next;
    };
    let next;
    try {
      next = await load(changedCart);
    } catch (error) {
      // A rendering bundled with a write can disagree with its verified read when
      // the cart changed elsewhere in between; fall back to one fresh render.
      if (!changedCart) throw error;
      next = await load(undefined);
    }
    if (!next) return;
    const nextNote = next.querySelector('form')?.elements.namedItem('note');
    if (draft !== undefined && nextNote) nextNote.value = draft;
    next.dataset.cartPricing = 'ready';
    for (const input of next.querySelectorAll('[data-cart-linked-parent]')) input.readOnly = false;
    const outsideFocus =
      document.activeElement && document.activeElement !== document.body && !root.contains(document.activeElement);
    if (root.closest('meri-cart-drawer')) prepareCartDrawerContent(next);
    root.replaceWith(next);
    root = next;
    state.root = next;
    pricingStates.set(next, state);
    restoreCartPropertyText(next);
    if (focused && !outsideFocus) {
      let target;
      if (lineReference) {
        const matches = [...next.querySelectorAll('[data-cart-line]')].filter((line) =>
          lineReference.lineId ? line.dataset.lineId === lineReference.lineId : line.dataset.key === lineReference.key,
        );
        const lines = [...next.querySelectorAll('[data-cart-line]')];
        const variantMatches =
          !lineReference.lineId && lineReference.variantId && lineReference.uniqueVariant
            ? lines.filter((line) => line.dataset.variantId === lineReference.variantId)
            : [];
        const line =
          (matches.length === 1 ? matches[0] : matches.find((node) => node.dataset.key === lineReference.key)) ||
          (variantMatches.length === 1 ? variantMatches[0] : null);
        if (line && lineReference.selector) target = line.querySelector(lineReference.selector);
      } else
        target = focused.id
          ? [...next.querySelectorAll('[id]')].find((node) => node.id === focused.id)
          : focused.name
            ? [...next.querySelectorAll('[name]')].find((node) => node.name === focused.name)
            : null;
      const fallback = next.querySelector('h1');
      if (target) {
        target.focus({ preventScroll: true });
        if (selection && target === nextNote) target.setSelectionRange(...selection);
      } else if (fallback) {
        fallback.tabIndex = -1;
        fallback.focus({ preventScroll: true });
      }
    }
    return next;
  } catch {
    if (!root.isConnected || state.cancelled) return;
    clearPricingSchedule(state);
    root.dataset.cartPricing = 'failed';
    form?.removeAttribute('aria-busy');
    if (message)
      showCartRetry(message, globalThis.MeriStrings?.cartError || 'We could not confirm the cart. Please retry.');
  } finally {
    state.pending = false;
    if (state.refreshRequested && !state.cancelled) scheduleCartPricing(root);
  }
}
// One in-place recovery for every cart failure: re-read Shopify's rendered cart
// (never repeat the write) without leaving the page or the drawer.
function showCartRetry(message, text) {
  message.textContent = text;
  const retry = document.createElement('button');
  retry.type = 'button';
  retry.className = 'button button--secondary';
  retry.dataset.cartPricingRetry = '';
  retry.textContent = globalThis.MeriStrings?.refreshCart || 'Refresh cart';
  message.append(' ', retry);
  return retry;
}
function scheduleCartPricing(root, boundary = false) {
  if (!root?.isConnected || root.dataset.cartPricing === 'failed') return;
  let state = pricingStates.get(root);
  if (!state || state.cancelled) {
    state = { root };
    pricingStates.set(root, state);
  }
  const drawer = root.closest('meri-cart-drawer');
  if (drawer && !drawer.querySelector('dialog')?.open) {
    clearPricingSchedule(state);
    // Keep the last Shopify-rendered content, but refresh before using it again.
    if (!state.pending) root.dataset.cartPricing = 'stale';
    else state.refreshRequested = true;
    return;
  }
  // Quantity/removal already owns the form and will reload its verified result.
  if (root.querySelector('form')?.dataset.pending === 'true' && root.dataset.cartPricing !== 'pending') return;
  if (boundary) {
    clearBoundaryChecks(state);
    // Read Shopify again after a boundary; these are bounded checks, not a
    // guarantee that native discount propagation completes within 30 seconds.
    state.boundaryTimers = [5000, 30000].map((delay) =>
      setTimeout(() => {
        if (!state.cancelled) scheduleCartPricing(state.root);
      }, delay),
    );
  }
  state.refreshRequested = true;
  if (document.hidden || state.pending || state.timer != null) return;
  if (deferQuantityDraft(root, state)) return;
  root.dataset.cartPricing = 'pending';
  // Campaigns crossing the same boundary share one native recalculation.
  state.timer = setTimeout(() => {
    state.timer = null;
    if (!state.cancelled) refreshCartPricing(root);
  }, 0);
}
document.addEventListener('meri:promotion:phase', (event) => {
  if (!event.detail?.from || event.detail.from === event.detail.to) return;
  for (const root of document.querySelectorAll('[data-cart-page]')) scheduleCartPricing(root, true);
});
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) for (const root of document.querySelectorAll('[data-cart-page]')) scheduleCartPricing(root);
});
function mountCartPricing(scope = document) {
  const roots = [...scope.querySelectorAll('[data-cart-page]')];
  if (scope.matches?.('[data-cart-page]')) roots.unshift(scope);
  for (const root of roots) {
    const drawer = root.closest('meri-cart-drawer');
    if (drawer && !drawer.querySelector('dialog')?.open) scheduleCartPricing(root);
    else refreshCartPricing(root);
  }
}
mountCartPricing();
window.addEventListener('pageshow', (event) => {
  if (event.persisted) mountCartPricing();
});
window.addEventListener('pagehide', () => {
  for (const root of document.querySelectorAll('[data-cart-page]')) {
    const state = pricingStates.get(root);
    if (state) {
      state.cancelled = true;
      clearPricingSchedule(state);
    }
  }
});
document.addEventListener('shopify:section:load', (event) => mountCartPricing(event.target));
export function unmountCartPricing(scope) {
  const roots = [...scope.querySelectorAll('[data-cart-page]')];
  if (scope.matches?.('[data-cart-page]')) roots.unshift(scope);
  for (const root of roots) {
    const state = pricingStates.get(root);
    if (state) {
      state.cancelled = true;
      clearPricingSchedule(state);
    }
  }
}
export function invalidateCartPricing(root) {
  if (root) scheduleCartPricing(root);
}
export function suspendCartPricing(scope) {
  for (const root of scope.querySelectorAll('[data-cart-page]')) {
    const state = pricingStates.get(root);
    clearTimeout(state?.timer);
    if (state) state.timer = null;
    // A recent promotion boundary can still be propagating at Shopify. Closing
    // cancels its checks, but must not promote that provisional snapshot to valid.
    if (state?.boundaryTimers?.length) {
      if (state.pending) state.refreshRequested = true;
      else root.dataset.cartPricing = 'stale';
    }
    clearBoundaryChecks(state);
  }
}
document.addEventListener('shopify:section:unload', (event) => unmountCartPricing(event.target));
async function updateLine(control, quantity) {
  if (!Number.isInteger(quantity) || quantity < 0 || control.disabled) return;
  if (control.closest('[data-cart-page]')?.dataset.cartPricing === 'pending') return;
  const line = control.closest('[data-cart-line]');
  const root = control.closest('[data-cart-page]');
  const region = root.querySelector('[data-cart-message]');
  const cartForm = line.closest('form');
  if (cartForm.dataset.pending === 'true') return;
  const state = pricingStates.get(root);
  clearPricingSchedule(state);
  // inert drops focus to <body>; record it first so the refreshed rows can restore it.
  if (state && root.contains(document.activeElement)) state.deferredFocus = { node: document.activeElement };
  cartForm.dataset.pending = 'true';
  cartForm.inert = true;
  cartForm.setAttribute('aria-busy', 'true');
  // The changed row shows immediate, local feedback (CSS delays the dim so fast writes never flash).
  // 3.0.4 (Owner): add-ons listed under a removed add-on leave with it (cart.js), so they show it too.
  const leaving = quantity
    ? [line]
    : [line, ...root.querySelectorAll(`[data-cart-attached-to="${CSS.escape(line.dataset.key || '')}"]`)];
  for (const row of leaving) row.dataset.cartUpdating = '';
  if (region) region.textContent = globalThis.MeriStrings?.updating || 'Updating…';
  let standardUpdate;
  try {
    const { cart } = await loadCartModule();
    standardUpdate = globalThis.MeriStandardEvents?.cart(
      control.closest('[data-cart-page]'),
      quantity ? 'update' : 'remove',
      'cart',
      [{ id: line.dataset.key, quantity }],
    );
    const context = new URL(location.href);
    for (const key of ['sections', 'section_id']) context.searchParams.delete(key);
    // Cart page and drawer share one path: Shopify renders the cart section with the
    // change (Section Rendering API), so the page updates in place instead of reloading.
    const updated = await cart.changeLine(
      { key: line.dataset.key, lineId: line.dataset.lineId },
      { quantity },
      {
        sectionId: root.dataset.cartSection,
        sectionsUrl: context.pathname + context.search,
      },
    );
    standardUpdate?.resolve(updated);
    // This value has already been submitted successfully; it is no longer
    // an unsent draft. Keep the marker through a failed pricing refresh so
    // Retry can fetch the authoritative section without repeating the write.
    if (control.matches('[data-cart-quantity]')) control.dataset.cartCommitted = control.value;
    cartForm.dataset.pending = 'false';
    await refreshCartPricing(root, updated, { force: true });
  } catch (error) {
    standardUpdate?.reject(error);
    root.dataset.cartPricing = 'failed';
    cartForm.dataset.pending = 'false';
    cartForm.inert = false;
    cartForm.removeAttribute('aria-busy');
    for (const row of leaving) delete row.dataset.cartUpdating;
    if (error.code === 'rejected') {
      const refreshed = await refreshCartPricing(root, undefined, { force: true });
      const refreshedMessage = refreshed?.querySelector('[data-cart-message]');
      if (refreshedMessage) {
        refreshedMessage.textContent = error.message;
        refreshedMessage.tabIndex = -1;
        refreshedMessage.focus({ preventScroll: true });
        return;
      }
    }
    // Even a rejected quantity can be partially applied by Shopify. Keep stale
    // totals out of checkout until the server-rendered cart has been refreshed.
    if (region) {
      showCartRetry(
        region,
        error.code === 'rejected' ? error.message : globalThis.MeriStrings?.cartError || 'Review your cart and retry.',
      ).focus();
    }
  }
}

function restoreCartPropertyText(root) {
  for (const source of root.querySelectorAll('template[data-cart-property-values]')) {
    const line = source.closest('[data-cart-line]');
    const values = line?.querySelectorAll('[data-cart-property-key]');
    if (!values?.length) continue;
    try {
      // Shopify Liquid can normalize text containing /uploads/ as a URL. The
      // line item's JSON keeps the submitted value. Use only its properties.
      const { properties } = JSON.parse(source.content.textContent);
      if (!properties || typeof properties !== 'object' || Array.isArray(properties)) continue;
      for (const value of values) {
        const key = value.dataset.cartPropertyKey;
        if (Object.hasOwn(properties, key) && typeof properties[key] === 'string') {
          value.textContent = properties[key];
          value.removeAttribute('data-cart-property-key');
        }
      }
    } catch {
      // Keep this line's checkout-review notice; other cart lines still work.
    }
  }
}

restoreCartPropertyText(document);
document.addEventListener('shopify:section:load', (event) => restoreCartPropertyText(event.target));
